// SEO summary → a branded, print-ready HTML document the designer can "Download PDF" (via the
// browser's native print → Save as PDF, so the text stays crisp/selectable — no raster).
//
// DETERMINISTIC + CLIENT-SIDE: a pure render of the already-loaded SeoClientSummary (no backend,
// no auth surface, no metering — the data is already in the browser). House look mirrors the
// analytics report (report-html.ts): cool monochrome, the v2 Alchmy families (Bricolage Grotesque /
// Sorts Mill Goudy) with web-safe fallbacks, the hosted wordmark. Alchmy's OWN brand, not the
// client's. Client-facing content ONLY — the technical "builder detail" is deliberately omitted.
// All dynamic text is HTML-escaped (the summary is model-authored).

import type { SeoClientSummary } from '../components/SeoSummary';
import { cleanSeoSummary } from './normalizeCopy';

// Cool minimalist monochrome (matches report-html.ts).
const INK = '#1a1a1a';
const MUTED = '#6b6b6b';
const LABEL = '#9a9a9a';
const RULE = '#e8e8e8';
const ACCENT_BG = '#f4f4f4';
const SANS = "'Bricolage Grotesque', -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif";
const SERIF = "'Sorts Mill Goudy', Georgia, 'Times New Roman', serif";

const SCHEMA_PLAIN: Record<string, string> = {
  LocalBusiness: 'a local business',
  Organization: 'an organisation',
  Corporation: 'a company',
  ProfessionalService: 'a professional service',
  Store: 'a shop',
  Restaurant: 'a restaurant',
};

function plainSchema(t: string): string {
  return SCHEMA_PLAIN[t] ?? `a ${t.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}`;
}

function pageName(path: string): string {
  if (path === '/' || path === '') return 'Home';
  const last = path.replace(/\/$/, '').split('/').filter(Boolean).pop() ?? path;
  return last.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function listWords(xs: string[]): string {
  return xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

function esc(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function assetBase(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://build.alchmy.studio';
  return origin.replace(/\/+$/, '');
}

function block(label: string, inner: string): string {
  return `<section style="padding:20px 0;border-top:1px solid ${RULE};page-break-inside:avoid;">
    <div style="font-family:${SANS};font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${LABEL};">${esc(label)}</div>
    <div style="margin-top:8px;">${inner}</div>
  </section>`;
}

/** Render the client-facing SEO summary into a full, self-printing, branded HTML document. */
export function seoReportHtml(rawSummary: SeoClientSummary): string {
  // Self-defending: normalise the copy here too (em-dashes → hyphen, invisible/"watermark" Unicode
  // scrubbed) so the PDF is clean regardless of the caller. Idempotent with the SeoSummary render.
  const summary = cleanSeoSummary(rawSummary);
  const base = assetBase();
  const businessName = esc(summary.businessName ?? 'Your business');
  const areaServed = summary.areaServed ?? [];
  const keywords = summary.keywords ?? [];
  const pages = summary.pages ?? [];
  const competitors = summary.competitors ?? [];

  const positioning = summary.positioningHeadline
    ? block(
        'Your positioning',
        `<p style="margin:0;font-family:${SERIF};font-size:20px;line-height:1.4;color:${INK};">${esc(summary.positioningHeadline)}</p>`,
      )
    : '';

  const whereInner = `<p style="margin:0;font-family:${SANS};font-size:15px;line-height:1.55;color:${MUTED};">We’ve set you up as <b style="color:${INK};">${esc(plainSchema(summary.schemaType))}</b>${
    areaServed.length > 0 ? ` serving <b style="color:${INK};">${esc(listWords(areaServed))}</b>` : ''
  }.</p>`;
  const where = block('Where you’ll show up', whereInner);

  const keywordsInner = `<p style="margin:0 0 10px;font-family:${SANS};font-size:13px;color:${MUTED};">The ${keywords.length} searches we’re optimising your site for:</p>
    <div>${keywords
      .map(
        (k) =>
          `<span style="display:inline-block;margin:0 6px 6px 0;padding:3px 10px;background:${ACCENT_BG};border-radius:999px;font-family:${SANS};font-size:12px;color:${INK};">${esc(k.term)}</span>`,
      )
      .join('')}</div>`;
  const keywordsBlock = keywords.length > 0 ? block('What your customers are searching for', keywordsInner) : '';

  const pagesInner = pages
    .map(
      (p) =>
        `<div style="margin-bottom:12px;padding:12px 14px;background:${ACCENT_BG};border-radius:6px;page-break-inside:avoid;">
          <div style="font-family:${SANS};font-size:10px;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:${LABEL};">${esc(pageName(p.path))}</div>
          <div style="margin-top:3px;font-family:${SANS};font-size:15px;font-weight:600;color:${INK};">${esc(p.title)}</div>
          <div style="margin-top:2px;font-family:${SANS};font-size:13px;line-height:1.5;color:${MUTED};">${esc(p.meta)}</div>
        </div>`,
    )
    .join('');
  const pagesBlock = pages.length > 0 ? block('Your pages in search results', pagesInner) : '';

  const competitorsInner = `<ul style="margin:0;padding-left:18px;font-family:${SANS};font-size:14px;line-height:1.6;color:${MUTED};">
    ${competitors
      .map(
        (c) =>
          `<li style="margin:2px 0;"><span style="color:${INK};font-weight:600;">${esc(c.name)}</span>${c.note ? ` - ${esc(c.note)}` : ''}</li>`,
      )
      .join('')}</ul>`;
  const competitorsBlock =
    competitors.length > 0 ? block('Who else shows up for these searches', competitorsInner) : '';

  const aiInner = summary.llmsSummary
    ? `<blockquote style="margin:0;padding-left:14px;border-left:2px solid ${RULE};font-family:${SERIF};font-size:16px;font-style:italic;line-height:1.5;color:${MUTED};">${esc(summary.llmsSummary)}</blockquote>`
    : '';
  const aiBlock = summary.llmsSummary ? block('Your summary for AI assistants', aiInner) : '';

  const c = summary.compliance;
  const complianceInner = c
    ? `<p style="margin:0;font-family:${SANS};font-size:15px;line-height:1.55;color:${INK};">${esc(c.posture)}</p>
       <p style="margin:6px 0 0;font-family:${SANS};font-size:13px;line-height:1.5;color:${MUTED};">${
         c.collectsPersonalData
           ? `You collect personal data; enquiries go to ${esc(c.enquiriesGoTo)}.`
           : 'No personal data is collected.'
       } Primary visitor region: ${esc(c.visitorRegion)}.</p>`
    : '';
  const complianceBlock = c ? block('Privacy &amp; compliance', complianceInner) : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${businessName} - SEO Summary</title>
<style>
  @page { margin: 18mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body {
    color: ${INK};
    font-family: ${SANS};
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .wrap { max-width: 720px; margin: 0 auto; padding: 40px 32px 56px; }
  .head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; padding-bottom: 22px; }
  .head img { height: 26px; width: auto; }
  .head .meta { text-align: right; font-family: ${SANS}; font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: ${LABEL}; }
  h1 { margin: 8px 0 4px; font-family: ${SERIF}; font-weight: 600; font-size: 30px; line-height: 1.2; color: ${INK}; }
  .sub { margin: 0 0 6px; font-family: ${SANS}; font-size: 14px; line-height: 1.5; color: ${MUTED}; }
  .foot { margin-top: 28px; padding-top: 16px; border-top: 1px solid ${RULE}; font-family: ${SANS}; font-size: 11px; color: ${LABEL}; }
  @media print { .wrap { padding: 0; } a { color: inherit; text-decoration: none; } }
</style>
</head>
<body>
  <div class="wrap">
    <div class="head">
      <img src="${base}/report/wordmark.png" alt="Alchmy Studio" />
      <div class="meta">SEO Summary</div>
    </div>

    <div style="border-top:1px solid ${RULE};padding-top:18px;">
      <div style="font-family:${SANS};font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${LABEL};">What we’ve set up</div>
      <h1>${businessName} - your search presence</h1>
      <p class="sub">How ${businessName} is set up to be found on Google and by AI assistants.</p>
    </div>

    ${positioning}
    ${where}
    ${keywordsBlock}
    ${pagesBlock}
    ${competitorsBlock}
    ${aiBlock}
    ${complianceBlock}

    <div class="foot">Prepared by Alchmy Studio · hello@alchmy.studio</div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
</body>
</html>`;
}
